/**
 * CompanyFormDialog — reiner Anlegen/Bearbeiten-Dialog (callback-basiert, testbar).
 * Slug-Regel gespiegelt aus AuthValidationRules.ValidSlug (lowercase/digits/hyphens).
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  Input,
  Select,
} from '@/shared/components';
import type { CompanyType, CreateCompanyRequest } from '@/shared/api';

const schema = z.object({
  name: z.string().min(1, 'Pflichtfeld').max(200, 'Max. 200 Zeichen'),
  slug: z
    .string()
    .min(1, 'Pflichtfeld')
    .max(80, 'Max. 80 Zeichen')
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/, 'Nur Kleinbuchstaben, Ziffern und Bindestriche'),
  type: z.enum(['Customer', 'CleaningCompany']),
});

type Values = z.infer<typeof schema>;

export type CompanyFormValues = CreateCompanyRequest;

const TYPE_LABELS: Record<CompanyType, string> = {
  Customer: 'Kunde',
  CleaningCompany: 'Reinigungsfirma',
};

export function CompanyFormDialog({
  open,
  onOpenChange,
  mode,
  initial,
  loading,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initial?: CompanyFormValues;
  loading?: boolean;
  onSubmit: (values: CompanyFormValues) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: initial ?? { name: '', slug: '', type: 'Customer' },
  });

  useEffect(() => {
    if (open) reset(initial ?? { name: '', slug: '', type: 'Customer' });
  }, [open, initial, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogHeader>
            <DialogTitle>{mode === 'create' ? 'Neue Firma' : 'Firma bearbeiten'}</DialogTitle>
            <DialogDescription>
              Eine Firma bündelt Apartment-Zuweisungen — alle ihre Mitglieder erben sie.
            </DialogDescription>
          </DialogHeader>
          <DialogBody>
            <div className="grid gap-4">
              <FormField label="Name" error={errors.name?.message} required>
                <Input {...register('name')} autoFocus />
              </FormField>
              <FormField
                label="Kürzel"
                error={errors.slug?.message}
                hint="Eindeutig in diesem Workspace — Kleinbuchstaben, Ziffern und Bindestriche."
                required
              >
                <Input {...register('slug')} placeholder="mueller-immobilien" />
              </FormField>
              <FormField label="Art" error={errors.type?.message} required>
                <Select {...register('type')}>
                  <option value="Customer">{TYPE_LABELS.Customer}</option>
                  <option value="CleaningCompany">{TYPE_LABELS.CleaningCompany}</option>
                </Select>
              </FormField>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              className="min-h-11"
              onClick={() => onOpenChange(false)}
            >
              Abbrechen
            </Button>
            <Button type="submit" variant="brand" className="min-h-11" loading={loading}>
              {mode === 'create' ? 'Firma anlegen' : 'Änderungen speichern'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export { TYPE_LABELS as COMPANY_TYPE_LABELS };
